import { useState } from 'react';
import { PROSPECTOR_ENGINE_URL } from '../lib/engineUrls';

interface PreviewResult {
  database?: {
    id: string;
    name?: string;
    description?: string;
  } | null;
  tagsByCategory?: Record<string, string[]>;
  relationTags?: Record<string, Array<{ id: string; name: string }>>;
  unmatchedTags?: Array<{ category: string | null; name: string }>;
  selectionRule?: {
    id?: string;
    name?: string;
  } | null;
}

export function NotionIntake() {
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [manualTags, setManualTags] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [status, setStatus] = useState<{ type: 'idle' | 'loading' | 'error' | 'success'; message?: string }>({
    type: 'idle'
  });
  const [createdUrl, setCreatedUrl] = useState('');

  const buildFormData = () => {
    const formData = new FormData();
    if (title) {
      formData.append('title', title);
    }
    if (note) {
      formData.append('note', note);
    }
    if (manualTags) {
      formData.append('manualTags', manualTags);
    }
    if (file) {
      formData.append('file', file);
    }
    return formData;
  };

  const handlePreview = async () => {
    if (!note && !file) {
      setStatus({ type: 'error', message: 'Provide a note or upload a file.' });
      return;
    }
    setStatus({ type: 'loading', message: 'Previewing routing...' });
    setCreatedUrl('');
    try {
      const response = await fetch(`${PROSPECTOR_ENGINE_URL}/api/notion-intake/preview`, {
        method: 'POST',
        body: buildFormData()
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Preview failed');
      }
      setPreview({
        database: data.database,
        tagsByCategory: data.tagsByCategory || {},
        relationTags: data.relationTags || {},
        unmatchedTags: data.unmatchedTags || [],
        selectionRule: data.selectionRule
      });
      setStatus({ type: 'success', message: 'Preview ready.' });
    } catch (error) {
      setStatus({ type: 'error', message: error instanceof Error ? error.message : 'Preview failed' });
    }
  };

  const handleSubmit = async () => {
    if (!note && !file) {
      setStatus({ type: 'error', message: 'Provide a note or upload a file.' });
      return;
    }
    setStatus({ type: 'loading', message: 'Creating Notion page...' });
    setCreatedUrl('');
    try {
      const response = await fetch(`${PROSPECTOR_ENGINE_URL}/api/notion-intake/submit`, {
        method: 'POST',
        body: buildFormData()
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Submission failed');
      }
      setPreview({
        database: data.database,
        tagsByCategory: data.tagsByCategory || {},
        relationTags: data.relationTags || {},
        unmatchedTags: data.unmatchedTags || [],
        selectionRule: data.selectionRule
      });
      setCreatedUrl(data.url || '');
      setStatus({ type: 'success', message: 'Page created successfully.' });
    } catch (error) {
      setStatus({ type: 'error', message: error instanceof Error ? error.message : 'Submission failed' });
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-[var(--text-pri)] px-6 py-10">
      <div className="max-w-4xl mx-auto space-y-8">
        <header className="space-y-2">
          <h1 className="text-3xl font-semibold">Notion Intake</h1>
          <p className="text-[var(--text-ter)]">
            Submit a note or file and let the rules choose the right Notion database and tags.
          </p>
        </header>

        <section className="space-y-4 border border-[var(--rule)] bg-[var(--bg)] p-6">
          <div className="grid gap-4">
            <label className="space-y-2 text-sm">
              <span className="text-[var(--text-ter)]">Title</span>
              <input
                className="w-full rounded border border-[var(--rule)] bg-slate-950 px-3 py-2 text-[var(--text-pri)]"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="Optional title"
              />
            </label>

            <label className="space-y-2 text-sm">
              <span className="text-[var(--text-ter)]">Note</span>
              <textarea
                className="w-full min-h-[160px] rounded border border-[var(--rule)] bg-slate-950 px-3 py-2 text-[var(--text-pri)]"
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="Paste or type a note here"
              />
            </label>

            <label className="space-y-2 text-sm">
              <span className="text-[var(--text-ter)]">Manual Tags (optional)</span>
              <input
                className="w-full rounded border border-[var(--rule)] bg-slate-950 px-3 py-2 text-[var(--text-pri)]"
                value={manualTags}
                onChange={(event) => setManualTags(event.target.value)}
                placeholder="Comma-separated tags (e.g., Subject: AI, Place: NYC)"
              />
            </label>

            <label className="space-y-2 text-sm">
              <span className="text-[var(--text-ter)]">File (txt, md, pdf)</span>
              <input
                className="w-full rounded border border-[var(--rule)] bg-slate-950 px-3 py-2 text-[var(--text-pri)]"
                type="file"
                accept=".txt,.md,.markdown,.pdf,text/plain,application/pdf"
                onChange={(event) => setFile(event.target.files?.[0] || null)}
              />
            </label>
          </div>

          <div className="flex flex-wrap gap-3 pt-2">
            <button
              className="px-4 py-2 rounded bg-[var(--bg-raised)] hover:bg-[var(--bg-raised)] transition"
              type="button"
              onClick={handlePreview}
            >
              Preview Routing
            </button>
            <button
              className="px-4 py-2 rounded bg-[var(--slate)] hover:bg-[var(--slate)] transition"
              type="button"
              onClick={handleSubmit}
            >
              Create In Notion
            </button>
            {status.type !== 'idle' && (
              <div
                className={`px-3 py-2 rounded text-sm ${
                  status.type === 'error' ? 'bg-[var(--coral)] text-[var(--coral)]' : 'bg-[var(--green)] text-[var(--green)] dark:text-[var(--text-ter)]'
                }`}
              >
                {status.message}
              </div>
            )}
          </div>
        </section>

        {preview && (
          <section className="space-y-3 border border-[var(--rule)] bg-[var(--bg)] p-6">
            <h2 className="text-xl font-semibold">Preview</h2>
            <div className="text-sm text-[var(--text-ter)] space-y-2">
              <div>
                <span className="text-[var(--text-ter)]">Database:</span>{' '}
                {preview.database ? `${preview.database.name || 'Unnamed'} (${preview.database.id})` : 'None selected'}
              </div>
              {preview.selectionRule && (
                <div>
                  <span className="text-[var(--text-ter)]">Matched Rule:</span>{' '}
                  {preview.selectionRule.name || preview.selectionRule.id}
                </div>
              )}
              <div>
                <span className="text-[var(--text-ter)]">Tags:</span>{' '}
                {preview.tagsByCategory && Object.keys(preview.tagsByCategory).length > 0
                  ? Object.entries(preview.tagsByCategory)
                      .map(([category, tags]) => `${category}: ${tags.join(', ')}`)
                      .join(' • ')
                  : 'None'}
              </div>
              {preview.unmatchedTags && preview.unmatchedTags.length > 0 && (
                <div className="text-[var(--mustard)]">
                  Unmatched tags: {preview.unmatchedTags.map((tag) => tag.name).join(', ')}
                </div>
              )}
            </div>
            {createdUrl && (
              <a
                href={createdUrl}
                className="inline-flex items-center text-[var(--text-ter)] hover:text-[var(--text-ter)]"
                target="_blank"
                rel="noreferrer"
              >
                Open created Notion page
              </a>
            )}
          </section>
        )}
      </div>
    </div>
  );
}

